import { useFocusTrap, useOnClickOutside } from '@main/core-ui';
import type { KeyboardEvent, ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import type { PopoverInstance } from './usePopover';
import { StyledPopover } from './wrappers';

export interface AccessiblePopoverProps {
  /** A popover instance with functionality attributes */
  popover: PopoverInstance;
  /** Aria-label for the popover */
  label?: string;
  /** Children contents of the popover */
  children?: ReactNode;
  /** Vertical position of the popover */
  yPosition?: 'top' | 'bottom';
  /** Horizontal osition of the popover */
  xPosition?: 'left' | 'right';
}

/**
 * Popover component that was made from scratch using hooks and portal logic, if needed
 * Covers accessibility concerns such as keyboard navigability and focus-trapping
 */
export const AccessiblePopover = ({
  popover: { anchorElement, closePopover, canRender },
  label,
  children,
  yPosition = 'bottom',
  xPosition = 'right',
}: AccessiblePopoverProps): JSX.Element => {
  // State for setting the popover element
  const [popoverElement, setPopoverElement] = useState<HTMLDivElement>();
  // Set a provided element to be the popover element, called only once to prevent re-renders
  const onPopoverRefSet = useCallback(
    (el: HTMLDivElement) => setPopoverElement(el),
    [],
  );

  const onKeyUp = (e: KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Escape') {
      closePopover();
    }
  };

  useOnClickOutside(popoverElement, closePopover);

  useFocusTrap({
    element: popoverElement,
    onTrapped: () => {
      popoverElement?.focus({ preventScroll: true });
    },
  });

  useEffect(() => {
    if (popoverElement && anchorElement) {
      const anchorRect = anchorElement.getBoundingClientRect();
      const popoverRect = popoverElement.getBoundingClientRect();

      let left;
      if (xPosition === 'right') {
        left = anchorRect.right - popoverRect.width;
        // If the popover is too close to the left edge of the screen, center it
        if (left < 5) {
          left = (window.innerWidth - popoverRect.width) / 2;
        }
      } else {
        left = anchorRect.left;
        // If the popover is too close to the right edge of the screen, center it
        if (left + popoverRect.width + 5 > window.innerWidth) {
          left = (window.innerWidth - popoverRect.width) / 2;
        }
      }
      popoverElement.style.left = `${left}px`;

      let top;
      if (yPosition === 'bottom') {
        top = anchorRect.bottom + window.scrollY + 5;
      } else {
        top = anchorRect.top + window.scrollY - popoverRect.height;
      }
      popoverElement.style.top = `${top}px`;
    }
  }, [popoverElement, anchorElement, xPosition, yPosition]);

  return (
    <>
      {canRender
        ? createPortal(
            <StyledPopover
              ref={onPopoverRefSet}
              onKeyUp={onKeyUp}
              role="dialog"
              aria-label={label || 'Popover'}
              tabIndex={0}
            >
              {children}
            </StyledPopover>,
            document.getElementById('popover-root') as HTMLElement,
          )
        : undefined}
    </>
  );
};
